import { useAnalytics } from '@features/Analytics';
import { stripePaymentErrorAtom } from '@features/Stripe/atoms';
import {
  useStripePaymentRetryAnalytics,
  useStripeTryAnotherCard,
} from '@features/Stripe/hooks';
import { schedulePaymentRecovery } from '@features/Stripe/utils/schedule-payment-recovery';
import {
  getStripeRetryablePaymentErrorCode,
  isStripeTryAnotherCardPaymentError,
} from '@features/Stripe/utils/stripe-payment-errors';
import { PaymentRetryDeclinedModal } from '@features/Stripe/widgets/payment-retry-declined-modal';
import * as Sentry from '@sentry/nextjs';
import type { PaymentRequest, Stripe } from '@stripe/stripe-js';
import { useSetAtom } from 'jotai';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
]);

type Props = {
  planInfo: {
    planName: string;
    fullPriceDiscount: number;
    currency: string;
    priceId: string;
  };
  id?: string;
  stripe: Stripe | null;
  height?: number;
  borderRadius?: number;
  clientSecret: string;
  handleSuccessPayment: ({
    paymentMethod,
  }: {
    paymentMethod: string;
  }) => Promise<void>;
  // handleStatusMessage: (message: string) => void;
  handleAddPaymentInfoEventsSend: ({
    paymentMethod,
  }: {
    paymentMethod: string;
  }) => void;
  onReady?: () => void;
};

export const PaymentRequestButton = memo(
  ({
    height = 48,
    borderRadius,
    stripe,
    id,
    clientSecret,
    planInfo,
    handleSuccessPayment,
    // handleStatusMessage,
    handleAddPaymentInfoEventsSend,
    onReady,
  }: Props) => {
    const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
      null,
    );
    const [paymentMethodResult, setPaymentMethodResult] = useState<
      string | null | undefined
    >(null);
    const paymentMethodResultRef = useRef<string | null | undefined>(null);
    const isRetryPaymentAttemptRef = useRef(false);
    const [showRetryDeclinedModal, setShowRetryDeclinedModal] = useState(false);
    const setPaymentError = useSetAtom(stripePaymentErrorAtom);
    const { trackGoogleEvent } = useAnalytics();
    const {
      resetRetryPaymentErrorEscalation,
      shouldEscalateRetryPaymentError,
      trackRetryPaymentShown,
      trackRetryPaymentTapped,
    } = useStripePaymentRetryAnalytics();
    const { showTryAnotherCardModal } = useStripeTryAnotherCard();

    useEffect(() => {
      paymentMethodResultRef.current = paymentMethodResult;
    }, [paymentMethodResult]);

    const getAnalyticsPaymentMethod = useCallback(
      () => paymentMethodResultRef.current ?? 'ApplePay/GooglePay',
      [],
    );

    const handlePaymentError = useCallback(
      ({ error }: { error: any; paymentMethod: string }) => {
        const wasRetryPaymentAttempt = isRetryPaymentAttemptRef.current;
        isRetryPaymentAttemptRef.current = false;
        const analyticsPaymentMethod = getAnalyticsPaymentMethod();
        const shouldShowTryAnotherCardModalByError =
          isStripeTryAnotherCardPaymentError({
            error,
            paymentIntent: error?.payment_intent,
          });
        const retryablePaymentErrorCode = getStripeRetryablePaymentErrorCode({
          error,
          paymentIntent: error?.payment_intent,
        });
        const shouldEscalateRetryErrorToTryAnotherCardModal =
          !shouldShowTryAnotherCardModalByError && retryablePaymentErrorCode
            ? shouldEscalateRetryPaymentError({
                errorCode: retryablePaymentErrorCode,
                isRetryPaymentAttempt: wasRetryPaymentAttempt,
                paymentMethod: analyticsPaymentMethod,
                planId: planInfo.priceId,
              })
            : false;
        const shouldShowTryAnotherCardModal =
          shouldShowTryAnotherCardModalByError ||
          shouldEscalateRetryErrorToTryAnotherCardModal;
        const shouldShowRetryDeclinedModal =
          !!retryablePaymentErrorCode && !shouldShowTryAnotherCardModal;

        Sentry.captureException(error);
        trackGoogleEvent({
          eventName: 'payment_error',
          options: {
            errorCode: error?.code,
            errorDeclineCode: error?.decline_code,
            paymentMethod: analyticsPaymentMethod,
            paymentSystem: 'Stripe',
          },
        });

        schedulePaymentRecovery(() => {
          setPaymentError((previous) => ({
            ...previous,
            declineCode: error?.decline_code ?? '',
            errorCode: error?.code ?? '',
            paymentSystem: 'Stripe',
            paymentMethod: analyticsPaymentMethod,
          }));

          if (shouldShowTryAnotherCardModal) {
            showTryAnotherCardModal({
              paymentMethod: analyticsPaymentMethod,
              planId: planInfo.priceId,
            });
          } else if (shouldShowRetryDeclinedModal) {
            trackRetryPaymentShown({
              paymentMethod: analyticsPaymentMethod,
              planId: planInfo.priceId,
            });
          }

          setShowRetryDeclinedModal(shouldShowRetryDeclinedModal);
        });
      },
      [
        getAnalyticsPaymentMethod,
        planInfo.priceId,
        setPaymentError,
        shouldEscalateRetryPaymentError,
        showTryAnotherCardModal,
        trackGoogleEvent,
        trackRetryPaymentShown,
      ],
    );

    const toStripeAmount = useCallback((amount: number, currency: string) => {
      return ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase())
        ? Math.round(amount)
        : Math.round(amount * 100);
    }, []);

    const createPaymentRequest = useCallback(() => {
      if (!stripe) return;

      const pr = stripe.paymentRequest({
        country: 'US',
        currency: planInfo.currency.toLowerCase(),
        total: {
          label: planInfo.planName,
          amount: toStripeAmount(planInfo.fullPriceDiscount, planInfo.currency),
        },
        disableWallets: ['link'],
        requestPayerName: false,
        requestPayerEmail: false,
      });

      // Check the availability of the Payment Request API.
      pr.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(pr);
          setPaymentMethodResult(
            Object.keys(result).find((method) => result[method]),
          );
          onReady?.();
        }
      });

      pr.on('paymentmethod', async (event) => {
        // Confirm the PaymentIntent without handling potential next actions (yet).
        const { paymentIntent, error: confirmError } =
          await stripe.confirmCardPayment(
            clientSecret,
            { payment_method: event.paymentMethod.id },
            { handleActions: false },
          );

        if (confirmError) {
          handlePaymentError({
            error: confirmError,
            paymentMethod: event.paymentMethod.id ?? 'ApplePay/GooglePay',
          });
          event.complete('fail');
          return;
        }

        // Report to the browser that the confirmation was successful, prompting
        // it to close the browser payment method collection interface.
        event.complete('success');

        // Check if the PaymentIntent requires any actions and, if so, let Stripe.js
        // handle the flow. If using an API version older than "2019-02-11"
        // instead check for: `paymentIntent.status === "requires_source_action"`.
        if (paymentIntent.status === 'requires_action') {
          // Let Stripe.js handle the rest of the payment flow.
          const { error } = await stripe.confirmCardPayment(clientSecret);
          if (error) {
            handlePaymentError({
              error,
              paymentMethod: event.paymentMethod.id ?? 'ApplePay/GooglePay',
            });
            return;
          }
        }

        resetRetryPaymentErrorEscalation();
        // The payment has succeeded -- show a success message to your customer.
        await handleSuccessPayment({
          paymentMethod: getAnalyticsPaymentMethod(),
        });
      });

      pr.on('cancel', () => {
        isRetryPaymentAttemptRef.current = false;
      });
    }, [
      stripe,
      planInfo.currency,
      planInfo.planName,
      planInfo.fullPriceDiscount,
      clientSecret,
      handlePaymentError,
      resetRetryPaymentErrorEscalation,
      handleSuccessPayment,
      toStripeAmount,
      getAnalyticsPaymentMethod,
      onReady,
    ]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: PaymentRequest should be recreated when payment details change.
    useEffect(() => {
      if (!stripe) {
        // We can't create a PaymentRequest until Stripe.js loads.
        return;
      }

      // Сброс предыдущего paymentRequest при изменении плана
      setPaymentRequest(null);
      createPaymentRequest();
    }, [
      stripe,
      createPaymentRequest,
      planInfo.currency,
      planInfo.planName,
      planInfo.fullPriceDiscount,
      clientSecret,
    ]);

    const handleCloseRetryDeclinedModal = useCallback(() => {
      setShowRetryDeclinedModal(false);
    }, []);

    const handleRetryPayment = useCallback(() => {
      const analyticsPaymentMethod = getAnalyticsPaymentMethod();

      trackRetryPaymentTapped({
        paymentMethod: analyticsPaymentMethod,
        planId: planInfo.priceId,
      });

      if (!paymentRequest || paymentRequest.isShowing()) {
        isRetryPaymentAttemptRef.current = false;
        return;
      }

      setShowRetryDeclinedModal(false);
      isRetryPaymentAttemptRef.current = true;
      handleAddPaymentInfoEventsSend({
        paymentMethod: analyticsPaymentMethod,
      });

      try {
        paymentRequest.show();
      } catch (error) {
        isRetryPaymentAttemptRef.current = false;
        Sentry.captureException(error);
      }
    }, [
      getAnalyticsPaymentMethod,
      handleAddPaymentInfoEventsSend,
      paymentRequest,
      planInfo.priceId,
      trackRetryPaymentTapped,
    ]);

    return paymentRequest ? (
      <>
        <div
          id={id}
          style={{
            borderRadius: borderRadius,
            height: `${height}px`,
            overflow: 'hidden',
          }}
        >
          <div
            style={{ height: '100%', width: '100%' }}
            key={`${planInfo.planName}-${planInfo.fullPriceDiscount}-${planInfo.currency}`}
            onClick={() => {
              handleAddPaymentInfoEventsSend({
                paymentMethod: getAnalyticsPaymentMethod(),
              });
              paymentRequest.show();
            }}
          />
        </div>
        <PaymentRetryDeclinedModal
          onClose={handleCloseRetryDeclinedModal}
          onRetry={handleRetryPayment}
          show={showRetryDeclinedModal}
        />
      </>
    ) : null;
  },
);

PaymentRequestButton.displayName = 'PaymentRequestButton';
